import { ClassImporterExporter, ImportMap } from "@app/shared/resources/class-importer-exporter.service";

import { DbHelper } from "@app/shared/model/db-object.model";

export interface Address {
    id: number;
    address: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
    coordinates?: string;
    timezonename?: string;
    timezoneid?: string;
    typeid?: number;
    area?: string;
    builderid?: number;
    communityid?: number;
    gateaccesscode?: string;
    formattedAddress?: string;
    directions?: string;
    crossstreet?: string;
    communityName?: string;
    builderName?: string;
    buildingTypeName?: string;
    addressType?: string;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface AddressData {
    id: number;
    address: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
    coordinates: string;
    timezonename: string;
    timezoneid: string;
    home_type_id: number;
    address_area: string;
    home_builder_id: number;
    home_community_id: number;
    gate_access_code: string;
    cross_street?: string;
    directions?: string;
    communityName?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export class AddressHelper extends DbHelper<Address, AddressData> {
    dbTypeName = "Address";
    dbTypeNamePlural = "Addresses";

    static readonly importMap: ImportMap<Address, AddressData> = [
        { i: "id", o: "id" },
        { i: "address", o: "address" },
        { i: "address2", o: "address2" },
        { i: "city", o: "city" },
        { i: "state", o: "state" },
        { i: "country", o: "country" },
        { i: "zipcode", o: "zipcode" },
        { i: "coordinates", o: "coordinates" },
        { i: "timezonename", o: "timezonename" },
        { i: "timezoneid", o: "timezoneid" },
        { i: "home_type_id", o: "typeid", skipExport: true },
        { i: "address_area", o: "area" },
        { i: "home_builder_id", o: "builderid", skipExport: true },
        { i: "home_community_id", o: "communityid", skipExport: true },
        { i: "gate_access_code", o: "gateaccesscode" },
        { i: "directions", o: "directions" },
        { i: "cross_street", o: "crossstreet" },
        { i: "communityName", o: "communityName" },
    ];

    importMap = AddressHelper.importMap;

    static fromDbObject(dbObject: AddressData): Address {
        return ClassImporterExporter.importObject(dbObject, AddressHelper.importMap);
    }

    static toDbObject(obj: Address): AddressData {
        return ClassImporterExporter.exportObject(obj, AddressHelper.importMap);
    }

    static readonly ManageCountries = {
        countriesWithNoPostalCodes: ["Trinidad and Tobago", "United Arab Emirates", "Qatar"],
        countriesWithNoStates: ["Guam"],
    };

    static toString({ address, address2, city, state, country, zipcode }: Address): string {
        return [address, address2, city, state, country, zipcode].filter(prop => prop !== null).join(", ");
    }

    static hasPostalCodes(country) {
        return this.ManageCountries.countriesWithNoPostalCodes.every(noPostalCode => noPostalCode !== country);
    }
    static hasStates(country) {
        return this.ManageCountries.countriesWithNoStates.every(noState => noState !== country);
    }
    static isValid({ address, city, state, country, zipcode, area }: Address): boolean {
        let validAddress = !!address && !!city && !!country;
        if (this.hasStates(country)) {
            validAddress = validAddress && !!state;
        }
        if (this.hasPostalCodes(country)) {
            validAddress = validAddress && !!zipcode;
        }

        if (area) {
            validAddress = !!/[0-9,.]/.exec(area)?.length;
        }

        return validAddress;
    }
}

const ADDRESS_HELPER_SINGLETON: AddressHelper = new AddressHelper();
export { ADDRESS_HELPER_SINGLETON as addressHelper };

export class AddressTestHelper {
    static readonly importMap = new AddressHelper().importMap;

    static getDefault(dbObject: Partial<AddressData> = {}): Address {
        return ClassImporterExporter.importObject(dbObject, this.importMap);
    }
}
